import { Icon } from "@mdi/react";

import { mdiFilter } from "@mdi/js";

import { EntityKind } from "models/entityKind";

import IssuesPanel, {
  ActionLabel,
  BrandSwitch,
  FilterButton,
  PanelIssue
} from "components/base/IssuesPanel";

interface RowFilterControl {
  active: boolean;
  onToggle: () => void;
}

interface OriginalValuesControl {
  show: boolean;
  onToggle: (value: boolean) => void;
}

interface ImportIssuesPanelProps {
  /** Data cells folded to "n/a" from a genuine Excel error token. 0 hides the errors row. */
  errorCellCount: number;
  /** Omit to render the errors row as a message only (no "Show errors only" button). */
  errorFilter?: RowFilterControl;
  /** "Show actual values" switch on the errors row — shows the original error tokens. */
  originalValues?: OriginalValuesControl;
  /** Rows whose key value could not be parsed. 0 hides the invalid-key row. */
  invalidUwiCount: number;
  /** Omit to render the invalid-key row without its "Show invalid only" button. */
  invalidFilter?: RowFilterControl;
  /** Data-bearing headerless columns awaiting a name. 0 hides the naming row. */
  unnamedColumnCount: number;
  /** Columns that resolve to the same name — a blocker. 0 hides the duplicate-name row. */
  duplicateNameCount: number;
  entityKind: EntityKind;
}

function keyUnit(entityKind: EntityKind, plural: boolean): string {
  const base = entityKind === EntityKind.Facility ? "Facility ID" : "UWI";
  return plural ? `${base}s` : base;
}

function errorRowTitle(count: number): string {
  return count === 1
    ? `1 cell with an error was converted to "n/a".`
    : `${count} cells with errors were converted to "n/a".`;
}

function invalidRowTitle(count: number, entityKind: EntityKind): string {
  return count === 1
    ? `1 invalid ${keyUnit(entityKind, false)} detected in the key column.`
    : `${count} invalid ${keyUnit(entityKind, true)} detected in the key column.`;
}

function namingRowTitle(count: number): string {
  return count === 1
    ? "1 column has no header — it'll be removed unless you name it."
    : `${count} columns have no header — they'll be removed unless you name them.`;
}

function duplicateRowTitle(count: number): string {
  return count === 1
    ? "1 pair of columns shares the same name — rename one so each column is unique."
    : `${count} pairs of columns share the same name — rename them so each column is unique.`;
}

export default function ImportIssuesPanel({
  errorCellCount,
  errorFilter,
  originalValues,
  invalidUwiCount,
  invalidFilter,
  unnamedColumnCount,
  duplicateNameCount,
  entityKind
}: ImportIssuesPanelProps) {
  const issues: PanelIssue[] = [];

  // The blocking duplicate-name error is the one the panel paints red
  if (duplicateNameCount > 0) {
    issues.push({
      key: "duplicate-name",
      testId: "duplicate-name-banner",
      variant: "error",
      title: duplicateRowTitle(duplicateNameCount),
      subtitle:
        "Column names must be unique — fix this before the import can be submitted."
    });
  }

  if (errorCellCount > 0) {
    const hasControls = Boolean(errorFilter || originalValues);
    issues.push({
      key: "error-conversion",
      testId: "error-conversion-banner",
      title: errorRowTitle(errorCellCount),
      subtitle: hasControls
        ? "Toggle the original values, or filter to just the rows with an error cell."
        : undefined,
      action: hasControls ? (
        <>
          {originalValues && (
            <>
              <ActionLabel>Show actual values</ActionLabel>
              <BrandSwitch
                size="small"
                checked={originalValues.show}
                onChange={originalValues.onToggle}
              />
            </>
          )}
          {errorFilter && (
            <FilterButton
              size="small"
              type={errorFilter.active ? "primary" : "default"}
              icon={<Icon path={mdiFilter} size={0.82} />}
              onClick={errorFilter.onToggle}>
              {errorFilter.active ? "Show all" : "Show errors only"}
            </FilterButton>
          )}
        </>
      ) : undefined
    });
  }

  if (invalidUwiCount > 0) {
    issues.push({
      key: "invalid-uwi",
      testId: "invalid-uwi-banner",
      title: invalidRowTitle(invalidUwiCount, entityKind),
      subtitle: "These rows will be skipped on import.",
      action: invalidFilter ? (
        <FilterButton
          size="small"
          type={invalidFilter.active ? "primary" : "default"}
          icon={<Icon path={mdiFilter} size={0.82} />}
          onClick={invalidFilter.onToggle}>
          {invalidFilter.active ? "Show all" : "Show invalid only"}
        </FilterButton>
      ) : undefined
    });
  }

  if (unnamedColumnCount > 0) {
    issues.push({
      key: "headerless-naming",
      testId: "headerless-naming-banner",
      title: namingRowTitle(unnamedColumnCount),
      subtitle: "Click a highlighted header cell in the table below to name each one."
    });
  }

  return <IssuesPanel testId="import-issues-panel" issues={issues} />;
}
